import React from 'react';
import { Box, Button, IconButton, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import ZoomableImage from '../../components/shared/ZoomableImage';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm6_u7_x6_relais';
const IMAGE_BASE = '/assets/drawable/';
const IMAGE_KEYS = ['img_0'];
const IMAGE_KEYS_DYNAMIC = ['img_0'];

interface RelaisInfo {
  name: string;
  titel: string;
  artikelnummer?: string;
  allgemeineBeschreibung: string;
  typSpezifikation: string;
  anwendungsbeispiele: string;
  auslegungsHinweiseSpezifisch: string;
  anschlussPrinzip: string;
  wichtigeParameter: string;
  hersteller?: string;
  sockelInfo?: string;
}

const relaisTypen: RelaisInfo[] = [
  {
    name: 'Printrelais Finder Serie 40 (24VDC)',
    titel: 'Miniatur-Printrelais – Finder Serie 40',
    artikelnummer: '40.52 – Beispiel',
    allgemeineBeschreibung: 'Kompaktes Relaistype für Steuersignale, potentialfreie Kontakte.',
    typSpezifikation: '2 Wechsler, Spule 24 V DC, Sockel optional.',
    anwendungsbeispiele: 'Kleinlasten schalten, Signalkopplung zwischen Steuerungen.',
    auslegungsHinweiseSpezifisch: 'Kontaktbelastbarkeit beachten, Freilaufdiode an Spule, Schaltkategorie (AC1/AC15).',
    anschlussPrinzip: 'A1/A2 Spule; Kontakte COM/NO/NC nach Schaltplan; ggf. Fassungen/Sockel.',
    wichtigeParameter: 'Spulenspannung, Anzugs-/Abfallspannung, Kontaktstrom, Lebensdauer.',
    hersteller: 'Finder',
    sockelInfo: 'Passende Sockel der Serie 95'
  },
  {
    name: 'Interfacerelais (z. B. ABB/Weidmüller)',
    titel: 'Interfacerelais mit Sockel',
    allgemeineBeschreibung: 'Hutschienenmodul zur galvanischen Trennung und Lastschaltung.',
    typSpezifikation: '1 Wechsler/Schließer je Kanal, LED und Freilaufdiode integriert.',
    anwendungsbeispiele: 'Anpassung von SPS-DO an höhere Lasten, Trennung von Potentialen.',
    auslegungsHinweiseSpezifisch: 'Kontaktkategorie passend wählen, Induktivlasten schützen.',
    anschlussPrinzip: 'Spulenseite A1/A2; Lastseite COM/NO/NC; eindeutige Klemmenbeschriftung.',
    wichtigeParameter: 'Spulenleistung, max. Schaltstrom/Spannung, EMV.',
    hersteller: 'ABB/Weidmüller'
  },
  {
    name: 'Leistungsschütz (Schütze)',
    titel: 'Leistungsschütze für Motor-/Leistungsstromkreise',
    allgemeineBeschreibung: 'Schaltgeräte zum Schalten hoher Ströme/Spannungen, häufig mit Hilfskontakten.',
    typSpezifikation: 'AC3/AC1 Kategorien, Spule 24 V DC/230 V AC je nach Ausführung.',
    anwendungsbeispiele: 'Motorstarter, Lasttrennungen, Netzumschaltungen.',
    auslegungsHinweiseSpezifisch: 'Auslegung nach Motorleistung, AC3-Kategorie, Kurzschlussschutz koordinieren.',
    anschlussPrinzip: 'Hauptkontakte L1/L2/L3 ↔ T1/T2/T3; Spule A1/A2; Hilfskontakte 13/14 etc.',
    wichtigeParameter: 'Nennbetriebsstrom Ie, Bemessungsbetriebsleistung, Spulendaten, mechanische/elektrische Lebensdauer.'
  }
];

const introBlocks = [
  {
    heading: 'Relais und Schütze – Grundlagen',
    body: 'Relais schalten Lasten über galvanisch getrennte Kontakte. Von Miniatur-Printrelais über Interfacerelais bis zu Leistungsschützen variiert das Einsatzspektrum und die Auslegung.'
  },
  {
    heading: 'Auslegung und Anschluss',
    body: 'Kontaktkategorien (AC1/AC3/AC15) beachten, Induktivlasten mit Schutzbeschaltung, passende Sockel/Fassungen nutzen, Spulenspannung korrekt versorgen.'
  }
];

const M6_U7_X6_Relais: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM6, canUploadImagesM6 } = useAuth();
  const canEditTexts = canEditTextsM6;
  const canUploadImages = canUploadImagesM6;
  const { override, refresh } = useUnitOverride(PAGE_ID);

  const getImageSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    let s = String(v).trim();
    if (s.includes('/')) {
      s = s.split('/').pop() || s;
    }
    return `/assets/unit_custom/${s}`;
  };

  const getDynamicImageSrc = (selectedType: string, idx: number, fallback: string) => {
    const key = `dyn_${selectedType}_img_${idx}`;
    return getImageSrc(key, fallback);
  };

  const [selectedType, setSelectedType] = React.useState<string>(relaisTypen[0].name);
  const current = relaisTypen.find(r => r.name === selectedType) || relaisTypen[0];

  const mergedIntroBlocks = React.useMemo(
    () => introBlocks.map((tb, i) => ({
      ...tb,
      body: override?.texts?.[`intro_${i}`] || tb.body
    })),
    [override]
  );

  const mergedDynamicBlocks = React.useMemo(() => [
    { heading: current.titel, body: override?.texts?.[`dyn_${selectedType}_0`] || current.allgemeineBeschreibung },
    { heading: 'Typ / Spezifikation', body: override?.texts?.[`dyn_${selectedType}_1`] || current.typSpezifikation },
    { heading: 'Anwendungsbeispiele', body: override?.texts?.[`dyn_${selectedType}_2`] || current.anwendungsbeispiele },
    { heading: 'Auslegung – Hinweise', body: override?.texts?.[`dyn_${selectedType}_3`] || current.auslegungsHinweiseSpezifisch },
    { heading: 'Anschlussprinzip', body: override?.texts?.[`dyn_${selectedType}_4`] || current.anschlussPrinzip },
    { heading: 'Wichtige Parameter', body: override?.texts?.[`dyn_${selectedType}_5`] || current.wichtigeParameter }
  ], [selectedType, override, current]);

  const allTextBlocks = React.useMemo(
    () => [...mergedIntroBlocks, ...mergedDynamicBlocks],
    [mergedIntroBlocks, mergedDynamicBlocks]
  );

  const fullPlainText = React.useMemo(() =>
    allTextBlocks.map(b => `${b.heading}. ${b.body}`).join(' '),
    [allTextBlocks]
  );

  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  const handleImageReplace = React.useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      await refresh();
    } catch (error) {
      console.error('Fehler beim Bild-Upload:', error);
      alert('Fehler beim Hochladen des Bildes.');
    }
  }, [token, refresh]);

  const handleTextSave = React.useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token);
      await refresh();
    } catch (error) {
      console.error('Fehler beim Text-Speichern:', error);
      alert('Fehler beim Speichern des Textes.');
    }
  }, [token, refresh]);

  const handleTypeChange = (event: any) => {
    setSelectedType(String(event.target.value));
  };

  const ttsButton = React.createElement(IconButton, {
    onClick: speaking ? stop : speak,
    'aria-label': 'Text vorlesen',
    sx: { ml: 1 }
  }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '6.7.6 Relais und Schütze', actions: ttsButton },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        IMAGE_KEYS.map((key) =>
          React.createElement(ZoomableImage, {
            key: key,
            src: getImageSrc(key, `${IMAGE_BASE}zzz_help.png`),
            alt: 'Relais/Schütz',
            label: 'Relais-Bild',
            onReplace: canUploadImages ? (file: File) => handleImageReplace(key, file) : undefined
          })
        )
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        React.createElement(EditableTextBlock, { key: 0, pageId: PAGE_ID, blockId: 'intro_0', heading: mergedIntroBlocks[0].heading, body: mergedIntroBlocks[0].body, onSave: (newBody: string) => handleTextSave('intro_0', newBody) }),
        React.createElement(EditableTextBlock, { key: 1, pageId: PAGE_ID, blockId: 'intro_1', heading: mergedIntroBlocks[1].heading, body: mergedIntroBlocks[1].body, onSave: (newBody: string) => handleTextSave('intro_1', newBody) }),
        React.createElement(FormControl, { fullWidth: true, sx: { mt: 2, mb: 2 } },
          React.createElement(InputLabel, { id: 'relais-select-label' }, 'Relaistyp auswählen'),
          React.createElement(Select, {
            labelId: 'relais-select-label',
            id: 'relais-select',
            value: selectedType,
            label: 'Relaistyp auswählen',
            onChange: handleTypeChange
          },
            ...relaisTypen.map((m) =>
              React.createElement(MenuItem, { key: m.name, value: m.name }, m.name)
            )
          )
        ),
        React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
          IMAGE_KEYS_DYNAMIC.map((key) =>
            React.createElement(ZoomableImage, {
              key: key,
              src: getDynamicImageSrc(selectedType, 0, `${IMAGE_BASE}zzz_help.png`),
              alt: 'Bild für Spinner-Typ',
              label: 'Spinner-Bild',
              onReplace: canUploadImages ? (file: File) => handleImageReplace(`dyn_${selectedType}_img_0`, file) : undefined
            })
          )
        ),
        React.createElement(EditableTextBlock, { key: `${selectedType}_0`, pageId: PAGE_ID, blockId: `dyn_${selectedType}_0`, heading: mergedDynamicBlocks[0]?.heading || 'Allgemeine Beschreibung', body: mergedDynamicBlocks[0]?.body || '', onSave: (newBody: string) => handleTextSave(`dyn_${selectedType}_0`, newBody) }),
        React.createElement(EditableTextBlock, { key: `${selectedType}_1`, pageId: PAGE_ID, blockId: `dyn_${selectedType}_1`, heading: 'Typ / Spezifikation', body: mergedDynamicBlocks[1]?.body || '', onSave: (newBody: string) => handleTextSave(`dyn_${selectedType}_1`, newBody) }),
        React.createElement(EditableTextBlock, { key: `${selectedType}_2`, pageId: PAGE_ID, blockId: `dyn_${selectedType}_2`, heading: 'Anwendungsbeispiele', body: mergedDynamicBlocks[2]?.body || '', onSave: (newBody: string) => handleTextSave(`dyn_${selectedType}_2`, newBody) }),
        React.createElement(EditableTextBlock, { key: `${selectedType}_3`, pageId: PAGE_ID, blockId: `dyn_${selectedType}_3`, heading: 'Auslegung – Hinweise', body: mergedDynamicBlocks[3]?.body || '', onSave: (newBody: string) => handleTextSave(`dyn_${selectedType}_3`, newBody) }),
        React.createElement(EditableTextBlock, { key: `${selectedType}_4`, pageId: PAGE_ID, blockId: `dyn_${selectedType}_4`, heading: 'Anschlussprinzip', body: mergedDynamicBlocks[4]?.body || '', onSave: (newBody: string) => handleTextSave(`dyn_${selectedType}_4`, newBody) }),
        React.createElement(EditableTextBlock, { key: `${selectedType}_5`, pageId: PAGE_ID, blockId: `dyn_${selectedType}_5`, heading: 'Wichtige Parameter', body: mergedDynamicBlocks[5]?.body || '', onSave: (newBody: string) => handleTextSave(`dyn_${selectedType}_5`, newBody) }),
        React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
          React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m6_u7_x99_quiz') }, 'Zum Quiz')
        )
      )
    )
  );
};

export default M6_U7_X6_Relais;


